import {
  ApplicationConfig,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideFormlyCore } from '@ngx-formly/core';
import Aura from '@primeuix/themes/aura';
import { provideNativeDateAdapter } from '@angular/material/core';
import { routes } from './app.routes';
import { providePrimeNG } from 'primeng/config';
import { provideAnimations } from '@angular/platform-browser/animations';
import { authInterceptor } from '@src/app/core/interceptors/auth.interceptor';
import { errorInterceptor } from '@src/app/core/interceptors/error.interceptor';
import { UserService } from '@src/app/core/services/user/user.service';
import { LayoutService } from '@src/app/core/util/services/layout.service';
import { FeaturesPermissionService } from '@src/app/core/services/features-permission.service';
import { AuthService } from '@src/app/core/services/auth.service';
import { NotificationSignalRService } from '@src/app/core/services/notifications/notification-signalr.service';
import { NotificationService } from '@src/app/core/services/notifications/notification.service';
import { FormlyRepeatSectionComponent } from '@src/app/shared/components/formly-repeat-section/formly-repeat-section.component';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: 'none',
          cssLayer: false,
        },
      },
      translation: {
        noFileChosenMessage: 'Nenhum arquivo selecionado',
      },
    }),
    provideAnimations(),
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor])),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json',
      }),
      fallbackLang: 'pt-br',
      lang: 'pt-br',
    }),
    provideNativeDateAdapter(),
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    provideFormlyCore({
      validationMessages: [
        { name: 'required', message: 'Este campo é obrigatório' },
        { name: 'eanInvalid', message: 'O EAN deve conter exatamente 13 dígitos válidos' },
        { name: 'eanActive', message: 'Este EAN já está cadastrado e ativo na base Consys' },
        { name: 'ncmInvalid', message: 'O NCM deve conter exatamente 8 dígitos' },
        { name: 'cnpjInvalid', message: 'CNPJ inválido' },
        { name: 'cpfInvalid', message: 'CPF inválido' },
        { name: 'pattern', message: 'Formato inválido' },
        {
          name: 'min',
          message: (err: { min?: number }, field: { templateOptions?: { min?: number } }) =>
            `Este campo deve ser maior ou igual a ${
              field.templateOptions?.min || err.min
            }`,
        },
        {
          name: 'max',
          message: (err: { max?: number }, field: { templateOptions?: { max?: number } }) =>
            `Este campo deve ser menor ou igual a ${
              field.templateOptions?.max || err.max
            }`,
        },
        {
          name: 'minlength',
          message: (
            err: { requiredLength?: number },
            field: { templateOptions?: { minLength?: number } },
          ) =>
            `Este campo deve ter no mínimo ${
              field.templateOptions?.minLength || err.requiredLength
            } caracteres`,
        },
        {
          name: 'maxlength',
          message: (
            err: { maxLength?: { requiredLength?: number } },
            field: { templateOptions?: { maxLength?: number } },
          ) =>
            `Este campo deve ter no máximo ${
              field.templateOptions?.maxLength || err.maxLength?.requiredLength
            } caracteres`,
        },
      ],
      types: [{ name: 'repeat', component: FormlyRepeatSectionComponent }],
    }),
    provideAppInitializer(() => inject(UserService).loadLocalUser()),
    provideAppInitializer(() => inject(LayoutService).loadColors()),
        provideAppInitializer(async () => {
      const authService = inject(AuthService);
      const featuresPermissionService = inject(FeaturesPermissionService);
      const notificationSignalR = inject(NotificationSignalRService);
      const notificationService = inject(NotificationService);

      if (authService.isUserAuthenticated()) {
        try {
          authService.loadEntityData();
          await featuresPermissionService.loadFromEndpoint();
          featuresPermissionService.startAutoRefresh();
          if (!authService.isExternalUser()) {
            await notificationSignalR.startIfAuthenticated();
            notificationService.refreshUnreadCount();
          }
        } catch (error) {
          console.error('[AppInitializer] Failed to load permissions on app init:', error);
        }
      }
    }),
  ],
};
